import React from 'react';

import Advantage from 'pages/Landings/components/Advantage';
import Advantages from 'pages/Landings/components/Advantages';
import {
    AssessmentIcon,
    CorrelationIcon,
    DataFeedsIcon,
    SignalIcon,
    StorageIcon
} from 'pages/Landings/components/Icons';
import Paragraph from 'pages/Landings/components/Paragraph';
import TopicContent from 'pages/Landings/components/TopicContent';
import TopicHeading from 'pages/Landings/components/TopicHeading';
import TopicTemplate from 'pages/Landings/components/TopicTemplate';

const DataFeeds: React.FC = () => {
    return (
        <TopicTemplate
            heading={
                <TopicHeading
                    title="Vulners Datafeeds"
                    description="Bring Vulnerability Intelligence Into Your Stack"
                    illustration={<DataFeedsIcon />}
                />
            }
            content={
                <TopicContent>
                    <Paragraph
                        primary="Bring Vulnerability Intelligence Into Your Stack"
                        secondary="Vulners Datafeeds give product teams and corporate security teams a direct line to correlated, up-to-date vulnerability intelligence - delivered as clean, linked records you can store internally for enrichment, analytics, and customer-facing features. You’re not stitching feeds; you’re ingesting one unified database that spans CVEs, vendor advisories, Linux distributions, Microsoft sources, exploits/PoCs, security news/blogs, KEV and standard metrics - all normalized and ready for your data lake, SIEM, or feature pipelines."
                    />
                    <Paragraph
                        primary="Efficient Ingestion, Built for Internal Use"
                        secondary="Sync full or delta Archive API collections on your schedule (ideal for backfills and routine updates), with stable IDs and add/update timestamps for safe upserts and reproducible analytics. Keep history, run comparisons, and hydrate models without custom scrapers or fragile glue. The design goal: make “fetch → store → use” straightforward for both product features and internal risk programs."
                    />
                    <Paragraph
                        primary="Breadth That Actually Moves the Needle"
                        secondary={
                            <>
                                {
                                    'Vulners actively tracks and correlates content from 200+ sources so you can see the full picture in one place - authoritative identifiers, vendor fixes, live exploit evidence, and context. Examples:'
                                }
                                <ul>
                                    <li>
                                        {
                                            'CVE & Catalogs: CVE List, NVD, and correlation with CISA KEV for “exploited in the wild” signals.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Vendor Advisories: Red Hat, Oracle, F5, Check Point - normalized so vendor-specific formats become consistent downstream.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Linux Distributions: Ubuntu USN, SUSE/openSUSE, Oracle Linux - distribution-native advisories for servers and containers.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Windows / Microsoft Sources: MSRC, MSKB, Microsoft Security (mssecure/MMPC) - from Patch Tuesday to Defender intel.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Exploits & PoCs: Exploit-DB, Metasploit, Packet Storm, Seebug, plus first-party PoC collection from GitHub/Gitee for early, high-signal exploit evidence.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Blogs & Security News: The Hacker News (THN), Schneier, Qualys Blog, HackRead, and more—pulled into the same schema and cross-linked to CVEs.'
                                        }
                                    </li>
                                </ul>
                            </>
                        }
                    />
                    <Paragraph
                        primary="What You Get, Practically"
                        secondary={
                            <ul>
                                <li>
                                    {
                                        'Normalized, linked JSON database export that connects CVEs ↔ vendor advisories ↔ patches ↔ exploit references, with standard fields (e.g., CVSS) and enrichment (e.g., EPSS, KEV) available for downstream use.'
                                    }
                                </li>
                                <li>
                                    {
                                        'Full + delta archives for reliable internal storage, historical baselining, and backtesting.'
                                    }
                                </li>
                                <li>
                                    {
                                        'Proven coverage scale so your dashboards, ticketing, and models reflect current, correlated reality - not stale snapshots.'
                                    }
                                </li>
                            </ul>
                        }
                    />
                </TopicContent>
            }
            advantages={
                <Advantages title="Why Vulners for Datafeeds?">
                    <Advantage
                        icon={<SignalIcon />}
                        title="Source depth + unique signals"
                        description="Broad vendor/distribution coverage plus first-party GitHub/Gitee PoC harvesting for earlier exploit visibility."
                    />
                    <Advantage
                        icon={<CorrelationIcon />}
                        title="Correlation that matters"
                        description="CVEs aligned with vendor fixes, KEV, and real exploit references—so prioritization maps to what’s truly dangerous now."
                    />
                    <Advantage
                        icon={<StorageIcon />}
                        title="Designed to be stored"
                        description="Archive cadence (Elasticsearch/MongoDB compatible), stable IDs, and timestamps make internal replication and reproducible analytics routine - not an ETL project."
                    />
                    <Advantage
                        icon={<AssessmentIcon />}
                        title="Audit-ready provenance"
                        description="Stable IDs, add/update timestamps, and original-source links make pipelines fully traceable—easy replays, rollbacks, and model retraining without brittle ETL."
                    />
                </Advantages>
            }
        />
    );
};

export default DataFeeds;
